import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import React, { useEffect, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import useToastAlert from "@/hooks/useToastAlert";
import { IVRVoiceConfiguration } from "../types";
import useTeams from "@/hooks/useTeams";

interface Props {
  show: boolean;
  onClose(): void;
  onSave(configuration: IVRVoiceConfiguration): void;
  existingConfiguration?: IVRVoiceConfiguration;
}

export default function IVRVoiceConfigurationModal(props: Props) {
  const api = useApi();
  const { teams } = useTeams();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const toastAlert = useToastAlert();

  const [formData, setFormData] = useState<Partial<IVRVoiceConfiguration>>({
    name: "",
    voiceProvider: "ElevenLabs",
    voiceId: "",
    model: "multilingual",
    speed: 1.0,
    stability: 0.5,
    similarity: 0.75,
    transcriptionProvider: "Deepgram",
    transcriptionLanguage: "en",
    speechModel: "nova-2",
    vocabularyHints: "",
    interruptSensitivity: "medium",
    interruptible: "true",
    reportInputDuringAgentSpeech: false,
    dtmfDetection: true,
    teamId: undefined,
    maxCallDurationSeconds: undefined,
    callDurationWarningSeconds: undefined,
    callDurationExceededMessage: undefined
  });

  useEffect(() => {
    if (props.existingConfiguration) {
      setFormData(props.existingConfiguration);
    } else {
      setFormData({
        name: "",
        voiceProvider: "ElevenLabs",
        voiceId: "",
        model: "multilingual",
        speed: 1.0,
        stability: 0.5,
        similarity: 0.75,
        transcriptionProvider: "Deepgram",
        transcriptionLanguage: "en",
        speechModel: "nova-2",
        vocabularyHints: "",
        interruptSensitivity: "medium",
        interruptible: "true",
        reportInputDuringAgentSpeech: false,
        dtmfDetection: true,
        teamId: undefined,
        maxCallDurationSeconds: undefined,
        callDurationWarningSeconds: undefined,
        callDurationExceededMessage: undefined
      });
    }
  }, [props.existingConfiguration, props.show]);

  async function handleSubmit() {
    if (!formData.name) {
      setError("Configuration name is required.");
      return;
    }

    if (!formData.voiceId) {
      setError("Voice ID is required.");
      return;
    }

    if (formData.callDurationWarningSeconds && !formData.maxCallDurationSeconds) {
      setError("Max Call Duration is required when Warning Time is set.");
      return;
    }

    if (
      formData.maxCallDurationSeconds &&
      formData.callDurationWarningSeconds &&
      formData.callDurationWarningSeconds >= formData.maxCallDurationSeconds
    ) {
      setError("Warning Time must be less than Max Call Duration.");
      return;
    }

    if (
      formData.callDurationExceededMessage &&
      formData.callDurationExceededMessage.length > 500
    ) {
      setError("Timeout Message cannot exceed 500 characters.");
      return;
    }

    setSending(true);
    setError("");

    try {
      const payload = {
        name: formData.name,
        voiceProvider: formData.voiceProvider,
        voiceId: formData.voiceId,
        model: formData.model,
        speed: formData.speed || 1.0,
        stability: formData.stability || 0.5,
        similarity: formData.similarity || 0.75,
        transcriptionProvider: formData.transcriptionProvider,
        transcriptionLanguage: formData.transcriptionLanguage,
        speechModel: formData.speechModel,
        vocabularyHints: formData.vocabularyHints || "",
        interruptSensitivity: formData.interruptSensitivity,
        interruptible: formData.interruptible,
        reportInputDuringAgentSpeech:
          formData.reportInputDuringAgentSpeech || false,
        dtmfDetection: formData.dtmfDetection || true,
        teamId: formData.teamId ?? null,
        maxCallDurationSeconds: formData.maxCallDurationSeconds ?? null,
        callDurationWarningSeconds: formData.callDurationWarningSeconds ?? null,
        callDurationExceededMessage: formData.callDurationExceededMessage ?? null
      };

      let response;
      if (props.existingConfiguration) {
        response = await api.messaging.put(
          `IVRVoiceConfigurations/${props.existingConfiguration.id}`,
          payload
        );
      } else {
        response = await api.messaging.post("IVRVoiceConfigurations", payload);
      }

      if (response.ok) {
        const savedConfiguration = await response.json();
        props.onSave(savedConfiguration);
        toastAlert.displayToast(
          Severity.Success,
          props.existingConfiguration
            ? "Voice configuration updated successfully."
            : "Voice configuration created successfully."
        );
        props.onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.title || "Failed to save voice configuration.");
      }
    } catch (e) {
      setError("An error occurred while saving.");
      console.error(e);
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal show={props.show} maxWidth="max-w-3xl">
      <ModalHeader title="IVR Voice Configuration" onClose={props.onClose} />
      <ModalBody>
        <div className="mb-4 text-sm text-gray-600">
          Configure the voice and transcription settings for your IVR system.
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration Name *
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Support Team Voice"
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              maxLength={256}
            />
            <p className="text-xs text-gray-500 mt-1">
              A descriptive name for this voice configuration.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team (Optional)
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.teamId || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  teamId: e.target.value ? Number(e.target.value) : undefined
                })
              }
            >
              <option value="">All Teams</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              Voice Settings
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Voice Provider
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.voiceProvider || ""}
                onChange={(e) =>
                  setFormData({ ...formData, voiceProvider: e.target.value })
                }
              >
                <option value="ElevenLabs">ElevenLabs</option>
                <option value="Azure">Azure</option>
                <option value="Google">Google</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Voice ID *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., EXAVITQu4mRLihs9xqJ9"
                value={formData.voiceId || ""}
                onChange={(e) =>
                  setFormData({ ...formData, voiceId: e.target.value })
                }
                maxLength={256}
              />
              <p className="text-xs text-gray-500 mt-1">
                The voice ID from your voice provider.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.model || ""}
                onChange={(e) =>
                  setFormData({ ...formData, model: e.target.value })
                }
              >
                <option value="multilingual">Multilingual</option>
                <option value="eleven_monolingual_v1">Monolingual V1</option>
                <option value="eleven_turbo_v2">Turbo V2</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Speed
                </label>
                <input
                  type="number"
                  min={0.5}
                  max={2}
                  step={0.1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.speed || 1.0}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      speed: Number(e.target.value)
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">0.5 - 2.0</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stability
                </label>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.stability || 0.5}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stability: Number(e.target.value)
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">0 - 1</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Similarity
                </label>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.similarity || 0.75}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      similarity: Number(e.target.value)
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">0 - 1</p>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              Transcription Settings
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transcription Provider
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.transcriptionProvider || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    transcriptionProvider: e.target.value
                  })
                }
              >
                <option value="Deepgram">Deepgram</option>
                <option value="Azure">Azure</option>
                <option value="Google">Google</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., en, es, fr"
                value={formData.transcriptionLanguage || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    transcriptionLanguage: e.target.value
                  })
                }
                maxLength={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Speech Model
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.speechModel || ""}
                onChange={(e) =>
                  setFormData({ ...formData, speechModel: e.target.value })
                }
              >
                <option value="nova-2">Nova-2</option>
                <option value="enhanced">Enhanced</option>
                <option value="base">Base</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vocabulary Hints (Optional)
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Comma-separated words for better recognition"
                rows={2}
                value={formData.vocabularyHints || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vocabularyHints: e.target.value
                  })
                }
                maxLength={1024}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interrupt Sensitivity
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.interruptSensitivity || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      interruptSensitivity: e.target.value
                    })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interruptible
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.interruptible || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      interruptible: e.target.value
                    })
                  }
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              Advanced Settings
            </h3>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="reportInputDuringAgentSpeech"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={formData.reportInputDuringAgentSpeech ?? false}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    reportInputDuringAgentSpeech: e.target.checked
                  })
                }
              />
              <label
                htmlFor="reportInputDuringAgentSpeech"
                className="ml-2 block text-sm text-gray-700"
              >
                Report Input During Agent Speech
              </label>
            </div>

            <div className="flex items-center mt-3">
              <input
                type="checkbox"
                id="dtmfDetection"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={formData.dtmfDetection ?? true}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dtmfDetection: e.target.checked
                  })
                }
              />
              <label
                htmlFor="dtmfDetection"
                className="ml-2 block text-sm text-gray-700"
              >
                DTMF Detection
              </label>
            </div>
          </div>
        </div>

        {/* Call Duration Limits Section */}
        <div className="border-t pt-4 mt-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Call Duration Limits
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Configure maximum call duration and warning settings. Leave empty for system defaults (30 min max, 60s warning).
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Call Duration (seconds)
              </label>
              <input
                type="number"
                min={60}
                max={7200}
                step={1}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1800 (default: 30 minutes)"
                value={formData.maxCallDurationSeconds ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maxCallDurationSeconds: e.target.value ? Number(e.target.value) : undefined
                  })
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                60-7200 seconds (1 minute to 2 hours).
                {formData.maxCallDurationSeconds && (
                  <span className="font-medium">
                    {" "}= {Math.floor(formData.maxCallDurationSeconds / 60)} minutes
                  </span>
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warning Time (seconds)
              </label>
              <input
                type="number"
                min={10}
                max={300}
                step={1}
                disabled={!formData.maxCallDurationSeconds}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  !formData.maxCallDurationSeconds ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                placeholder="60 (default)"
                value={formData.callDurationWarningSeconds ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    callDurationWarningSeconds: e.target.value ? Number(e.target.value) : undefined
                  })
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                10-300 seconds. Warning sent before max duration is reached.
                {formData.callDurationWarningSeconds && (
                  <span className="font-medium">
                    {" "}= {Math.floor(formData.callDurationWarningSeconds / 60)} min {formData.callDurationWarningSeconds % 60}s
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeout Message (Optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your call has exceeded the maximum duration. Thank you for contacting us."
              rows={3}
              maxLength={500}
              value={formData.callDurationExceededMessage ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  callDurationExceededMessage: e.target.value || undefined
                })
              }
            />
            <p className="text-xs text-gray-500 mt-1">
              Message played when max call duration is exceeded.{" "}
              <span className="font-medium">
                {formData.callDurationExceededMessage?.length ?? 0}/500 characters
              </span>
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2">
          <SecondaryBlueButton
            label="Cancel"
            onClick={props.onClose}
            disabled={sending}
          />
          <PrimaryBlueButton
            label={sending ? "Saving..." : "Save"}
            onClick={handleSubmit}
            disabled={sending}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
}
